import { useState } from "react";
import { CircleHelp, Plus } from "lucide-react";
import { Button, Modal } from "../components/ui";
import type { MobilePageProps, MobileRoute } from "./types";
import { MobilePrimaryButton, PageHeader, RecordEmpty } from "./shared";
import "./member-pages.css";
import { WalletPage } from "./WalletPage";
import { MoneyRequestPage } from "./MoneyRequestPage";

type MemberPagesProps = MobilePageProps & { route: MobileRoute; params?: URLSearchParams };

export function MemberPages(props: MemberPagesProps) {
  switch (props.route) {
    case "deposit":
    case "withdraw": return <MoneyRequestPage key={props.route} {...props} type={props.route} />;
    case "wallet": return <WalletPage key={props.profile?.user.id ?? "guest"} {...props} />;
    case "cards": return <CardsPage {...props} />;
    case "coin-guide": return <CoinGuidePage {...props} />;
    default: return null;
  }
}

function CardsPage({ onBack, onNavigate }: MobilePageProps) {
  const [help, setHelp] = useState(false);
  return <div className="member-page member-cards">
    <PageHeader title="银行卡" onBack={onBack} />
    <div className="member-page__content"><RecordEmpty message="暂无收款方式" /><MobilePrimaryButton type="button" onClick={() => setHelp(true)}><Plus size={21} aria-hidden="true" /> 添加收款方式</MobilePrimaryButton></div>
    <Modal open={help} title="收款方式绑定暂未开放" description="当前支持提交人工提现申请，收款信息由客服协助核实。" onClose={() => setHelp(false)}><p>请通过客服中心了解办理方式。当前页面不会收集或保存银行卡号、钱包地址与取款密码。</p><div className="modal-actions"><Button intent="neutral" emphasis="outline" onClick={() => setHelp(false)}>返回</Button><Button onClick={() => { setHelp(false); onNavigate("support"); }}>联系客服</Button></div></Modal>
  </div>;
}

const guideCards = [
  { title: "火币网", caption: "huobi.com", mark: "H", tone: "security", topic: "账户安全", body: "此平台专属教程暂未提供。通用提醒：为账号设置独立密码，不向他人提供密码、验证码或钱包助记词。通过已确认的网站和客服入口处理账户问题。" },
  { title: "币安", caption: "binance.com", mark: "B", tone: "network", topic: "网络选择", body: "此平台专属教程暂未提供。通用提醒：操作前核对收款方提供的币种和网络名称，发送方与接收方必须匹配。可用网络与地址以本站充值页当前通道为准，请先核对收款资料。" },
  { title: "欧易", caption: "okx.com", mark: "O", tone: "address", topic: "地址核对", body: "此平台专属教程暂未提供。通用提醒：逐项核对收款地址和收款方信息，不要仅凭地址前后几位判断，也不要使用聊天记录中来源不明的地址。" },
  { title: "中币", caption: "zb.com", mark: "Z", tone: "record", topic: "转账记录", body: "此平台专属教程暂未提供。通用提醒：保留订单号、操作时间和转账记录，出现问题时向官方客服提供查询凭据。不要公开密码、验证码或其他账户秘密。" },
];

function GuideMark({ mark }: { mark: string }) {
  return <svg viewBox="0 0 64 64" width="44" height="44" aria-hidden="true">
    {mark === "H" && <><path fill="#23234e" d="M32 2c5 16-8 19-15 31-8 14-1 23 14 28-8-8-10-14-5-24C34 23 44 16 32 2Z"/><path fill="#55a9d1" d="M42 23c-1 10-10 13-12 23-2 7 1 12 7 15 17-8 18-22 5-38Z"/></>}
    {mark === "B" && <g fill="#f3bb30"><path d="m32 3 17 17-7 7-10-10-10 10-7-7ZM3 32l9-9 9 9-9 9Zm29-9 9 9-9 9-9-9Zm11 9 9-9 9 9-9 9ZM15 44l7-7 10 10 10-10 7 7-17 17Z"/></g>}
    {mark === "O" && <><circle cx="32" cy="14" r="13" fill="#82c2ee"/><circle cx="14" cy="32" r="13" fill="#2871eb"/><circle cx="32" cy="50" r="13" fill="#2f61e3"/><circle cx="50" cy="32" r="13" fill="#589ef5"/><circle cx="32" cy="32" r="13" fill="#407bf1"/></>}
    {mark === "Z" && <g fill="none" stroke="#d52c29" strokeWidth="4.5" strokeLinecap="round"><circle cx="32" cy="32" r="25"/><path d="M19 27c1-11 20-13 26-4 5 8-3 16-12 14M43 37c-1 11-20 13-26 4-5-8 3-16 12-14M24 39l16-15"/></g>}
  </svg>;
}

function CoinGuidePage({ onBack, onNavigate }: MobilePageProps) {
  const [guide, setGuide] = useState<(typeof guideCards)[number] | null>(null);
  return <div className="member-page member-guide">
    <PageHeader title="购币指南" onBack={onBack} />
    <div className="member-page__content"><div className="member-guide-grid">{guideCards.map((item) => <article className="member-guide-card" key={item.title}><div className={`member-guide-icon member-guide-icon--${item.tone}`}><GuideMark mark={item.mark}/></div><h2>{item.title}</h2><p>{item.caption}</p><button type="button" onClick={() => setGuide(item)}>查看教程</button></article>)}</div><div className="member-guide-note"><CircleHelp size={15} aria-hidden="true" /><span>平台专属教程暂未提供，可查看通用操作说明。实际存提通道以充值、提现页面显示为准。</span></div></div>
    <Modal open={Boolean(guide)} title={guide ? `${guide.title} · ${guide.topic}` : "操作说明"} onClose={() => setGuide(null)}><p>{guide?.body}</p><div className="modal-actions"><Button intent="neutral" emphasis="outline" onClick={() => setGuide(null)}>知道了</Button><Button onClick={() => { setGuide(null); onNavigate("support"); }}>联系客服</Button></div></Modal>
  </div>;
}
